import "@/features/internationalisation";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet/dist/leaflet.css";
import type { AppProps } from "next/app";
import "../styles/antd-overrides.css";
import "../styles/globals.css";
import supabase from "@/features/supabase";
import { Session, SessionContextProvider, useSessionContext } from "@supabase/auth-helpers-react";
import { NextPage } from "next";
import Head from "next/head";
import { ReactNode, useEffect, useState } from "react";
import { Provider } from "react-redux";
import { ColorModeProvider } from "@/components/layouts/ColorModeContext";
import { CustomConfigProvider } from "@/components/layouts/CustomConfigProvider";
import { HubLayout } from "@/components/layouts/HubLayout";
import { NavigationTracker } from "@/components/layouts/NavigationTracker";
import { SidebarProvider } from "@/components/layouts/SidebarContext";
import { Loading } from "@/components/overlays/Loading";
import { getAppName, getTheme } from "@/features/branding";
import i18n from "@/features/internationalisation";
import { store } from "@/store";
import { KeyPairProvider } from "@/utils/hooks/useKeys/KeyPairProvider";
import { HubPlausibleProvider } from "@/features/tracking";
import "@fontsource-variable/jetbrains-mono";
const UserLoadingWrapper = ({
  children
}: {
  children: JSX.Element;
}): JSX.Element => {
  const {
    isLoading
  } = useSessionContext();
  return isLoading ? <Loading data-sentry-element="Loading" data-sentry-component="UserLoadingWrapper" data-sentry-source-file="_app.tsx" /> : children;
};
export type NextPageWithLayout<P = any, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};
type AppPropsWithLayout = AppProps & {
  initialSession: Session;
  Component: NextPageWithLayout;
};
function MyApp({
  Component,
  pageProps
}: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page: ReactNode) => <HubLayout>{page}</HubLayout>);
  const [canRender, setCanRender] = useState(false);

  /** Apply branding translation overrides */
  useEffect(() => {
    const language = i18n.language;
    const overrides: Record<string, any> = getTheme().translationOverrides?.[language] || {};
    for (const key of Object.keys(overrides)) {
      i18n.addResourceBundle(language, key, overrides[key], true, true);
    }
  }, []);
  const enableMocking = async () => {
    if (!process.env.NEXT_PUBLIC_MOCK_API_CALLS) {
      setCanRender(true);
      return;
    }
    const {
      worker
    } = await import("../tests/mocks/msw_browser");
    await worker.start();
    setCanRender(true);
    return () => worker.stop();
  };
  useEffect(() => {
    enableMocking();
  }, []);
  if (!canRender) {
    return;
  }
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
        <title>{getAppName()}</title>
        <meta name="description" content="Your home of climate insight" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <HubPlausibleProvider data-sentry-element="HubPlausibleProvider" data-sentry-source-file="_app.tsx">
        <Provider store={store} data-sentry-element="Provider" data-sentry-source-file="_app.tsx">
          {/* Outside CustomConfigProvider: it reads the mode to pick antd's algorithm. */}
          <ColorModeProvider data-sentry-element="ColorModeProvider" data-sentry-source-file="_app.tsx">
            <CustomConfigProvider data-sentry-element="CustomConfigProvider" data-sentry-source-file="_app.tsx">
              <SessionContextProvider supabaseClient={supabase} data-sentry-element="SessionContextProvider" data-sentry-source-file="_app.tsx">
                <KeyPairProvider data-sentry-element="KeyPairProvider" data-sentry-source-file="_app.tsx">
                  <SidebarProvider data-sentry-element="SidebarProvider" data-sentry-source-file="_app.tsx">
                    <NavigationTracker data-sentry-element="NavigationTracker" data-sentry-source-file="_app.tsx">
                      <UserLoadingWrapper data-sentry-element="UserLoadingWrapper" data-sentry-source-file="_app.tsx">
                        <>{getLayout(<Component {...pageProps} />)}</>
                      </UserLoadingWrapper>
                    </NavigationTracker>
                  </SidebarProvider>
                </KeyPairProvider>
              </SessionContextProvider>
            </CustomConfigProvider>
          </ColorModeProvider>
        </Provider>
      </HubPlausibleProvider>
    </>;
}
export default MyApp;